import { createSelector } from 'reselect'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faArrowsRotate,
  faFlag,
  faLock,
} from '@fortawesome/pro-solid-svg-icons'
import { ReduxState } from '@/utils/typeHelpers'
import { TimelineStepStatus, type TimelineStepBadge } from '@/ui'
import {
  AttentionState,
  CubkCardState,
  CubkStatusResponse,
} from './cubkProgress.types'

const selectCubkProgressSlice = (state: ReduxState) => state.cubkProgress

export const selectCubkProgressData = createSelector(
  selectCubkProgressSlice,
  (cubkProgress): CubkStatusResponse | null => cubkProgress.data
)

export const selectIsBankSyncInProgress = createSelector(
  selectCubkProgressData,
  (data): boolean =>
    Boolean(
      data?.attentionStates.includes(AttentionState.BANK_SYNC_IN_PROGRESS)
    )
)

export const selectCubkCardState = createSelector(
  selectCubkProgressData,
  (data): CubkCardState => {
    if (!data) return CubkCardState.SYNC_AND_CATEGORIZATION

    const { attentionStates } = data

    // Error states take priority
    if (
      attentionStates.includes(AttentionState.INSTITUTION_UNSUPPORTED) ||
      attentionStates.includes(AttentionState.BANK_SYNC_ERROR_RETRYABLE) ||
      attentionStates.includes(AttentionState.BANK_REAUTH_REQUIRED)
    ) {
      return CubkCardState.BANK_INCOMPATIBLE
    }

    // Both sync-related attention states map to the single combined step
    if (
      attentionStates.includes(AttentionState.BANK_SYNC_IN_PROGRESS) ||
      attentionStates.includes(AttentionState.CATEGORIZATION_IN_PROGRESS)
    ) {
      return CubkCardState.SYNC_AND_CATEGORIZATION
    }

    return CubkCardState.BK_TEAM_REVIEW
  }
)

export interface CubkTimelineStep {
  label: string
  status: TimelineStepStatus
  icon?: IconDefinition
  iconClassName?: string
  badge?: TimelineStepBadge
}

const TIMELINE_LABELS = [
  'Sync and auto-categorization',
  'Prepare for bookkeeping',
  'Bookkeeping review',
  'Books complete',
]

const WAITING_ON_HEARD_BADGE: TimelineStepBadge = {
  label: 'Waiting on Heard',
  color: 'gray',
}

const ACTION_REQUIRED_BADGE: TimelineStepBadge = {
  label: 'Action Required',
  color: 'orange',
}

export const buildTimelineSteps = (
  cardState: CubkCardState
): CubkTimelineStep[] => {
  const getStatuses = (): TimelineStepStatus[] => {
    switch (cardState) {
      case CubkCardState.SYNC_AND_CATEGORIZATION:
        return [
          TimelineStepStatus.InProgress,
          TimelineStepStatus.NotStarted,
          TimelineStepStatus.NotStarted,
          TimelineStepStatus.NotStarted,
        ]
      case CubkCardState.PREPARE_FOR_BOOKKEEPING:
        return [
          TimelineStepStatus.Complete,
          TimelineStepStatus.InProgress,
          TimelineStepStatus.NotStarted,
          TimelineStepStatus.NotStarted,
        ]
      case CubkCardState.BK_TEAM_REVIEW:
      case CubkCardState.BK_TEAM_REVIEW_AFTER_FOLLOWUP:
      case CubkCardState.YOUR_REVIEW:
        return [
          TimelineStepStatus.Complete,
          TimelineStepStatus.Complete,
          TimelineStepStatus.Review,
          TimelineStepStatus.NotStarted,
        ]
      case CubkCardState.COMPLETE:
        return [
          TimelineStepStatus.Complete,
          TimelineStepStatus.Complete,
          TimelineStepStatus.Complete,
          TimelineStepStatus.Complete,
        ]
      case CubkCardState.BANK_INCOMPATIBLE:
      default:
        return [
          TimelineStepStatus.NotStarted,
          TimelineStepStatus.NotStarted,
          TimelineStepStatus.NotStarted,
          TimelineStepStatus.NotStarted,
        ]
    }
  }

  const getBadgeForReviewStep = (): TimelineStepBadge | undefined => {
    switch (cardState) {
      case CubkCardState.BK_TEAM_REVIEW:
      case CubkCardState.BK_TEAM_REVIEW_AFTER_FOLLOWUP:
        return WAITING_ON_HEARD_BADGE
      case CubkCardState.YOUR_REVIEW:
        return ACTION_REQUIRED_BADGE
      default:
        return undefined
    }
  }

  const statuses = getStatuses()
  const reviewBadge = getBadgeForReviewStep()

  return TIMELINE_LABELS.map((label, index) => {
    const status = statuses[index]
    const step: CubkTimelineStep = { label, status }

    if (status === TimelineStepStatus.InProgress && index === 0) {
      step.icon = faArrowsRotate
      step.iconClassName = 'animate-spin'
    } else if (status === TimelineStepStatus.InProgress && index === 1) {
      step.icon = faFlag
    } else if (status === TimelineStepStatus.Review && index === 2) {
      step.icon = faLock
      step.badge = reviewBadge
    }

    return step
  })
}

export const selectCubkTimelineSteps = createSelector(
  selectCubkCardState,
  (cardState): CubkTimelineStep[] => buildTimelineSteps(cardState)
)

export interface CubkProgress {
  percentage: number
  totalTransactions: number
  categorizedCount: number
  incomeCategorized: number | null
  expensesCategorized: number | null
  taxDeductionsFound: number | null
}

export const selectCubkProgress = createSelector(
  selectCubkProgressData,
  selectCubkCardState,
  (data, cardState): CubkProgress => {
    const isUnavailable = !data || cardState === CubkCardState.BANK_INCOMPATIBLE

    if (isUnavailable) {
      return {
        percentage: 0,
        totalTransactions: 0,
        categorizedCount: 0,
        incomeCategorized: null,
        expensesCategorized: null,
        taxDeductionsFound: null,
      }
    }

    return {
      percentage: data.categorizedPercentage,
      totalTransactions: data.totalTransactions,
      categorizedCount: data.categorizedCount,
      incomeCategorized: data.incomeCategorized,
      expensesCategorized: data.expensesCategorized,
      taxDeductionsFound: data.taxDeductionsFound,
    }
  }
)

export const selectShouldStopPolling = createSelector(
  selectCubkProgressData,
  (_data): boolean => {
    // Never stop polling while on the onboarding dashboard.
    // The BK_TEAM_REVIEW state (empty attentionStates) still needs
    // updates when the bookkeeping team progresses the user.
    // Page navigation/unmount handles cleanup via usePollingWithBackoff.
    return false
  }
)
